import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react'
import styles from './styles.module.css'
import TextField from '@mui/material/TextField'
import LocationDropDown from '../../../../../global/components/location-drop-down/LocationDropDown'
import { ILocation } from '../../../../../../../app/entities/Location'
import { getLocationByLocationId } from '../../api'
import { fetchData } from '../../../../../global/utils/fetch'
import hasPermission from '../../../../../global/utils/user/has-permission'
import { UserContext } from '../../../../../base/components/base-container/BaseContainer'
import { PERMISSIONS } from '../../../../../global/constants/permissions'
import { formatDateYyyyMmDd } from '../../../../../global/utils/date/date-utils-yyyy-mm-dd'
import { ITechnicianPartial } from '../../../../../../../app/entities/Technician'

interface ITechnicianInputBarProps {
  technicianData: ITechnicianPartial
  setTechnicianData: Dispatch<SetStateAction<ITechnicianPartial>>
  location: ILocation | null
  setLocation: Dispatch<SetStateAction<ILocation | null>>
}

export default function TechInputBar({
  technicianData,
  setTechnicianData,
  location,
  setLocation,
}: ITechnicianInputBarProps) {
  const { user } = useContext(UserContext)

  const [shouldAllowLocationEdit, setShouldAllowLocationEdit] =
    useState<boolean>(false)

  const { employeeId, samsaraId, inactive, employmentStartDate } =
    technicianData

  function updateTechForm(e: ChangeEvent<HTMLInputElement>) {
    setTechnicianData({
      ...technicianData,
      [e.target.name]: e.target.value,
    })
  }

  function updateStartDate(e: ChangeEvent<HTMLInputElement>) {
    setTechnicianData({
      ...technicianData,
      employmentStartDate: new Date(e.target.value),
    })
  }

  async function updateLocation(locationObjectId: string) {
    const newLocation = await fetchData<ILocation>(
      getLocationByLocationId(locationObjectId),
    )
    setLocation(newLocation)
  }

  function activeStatusHandler(newStatus: boolean) {
    setTechnicianData({ ...technicianData, inactive: newStatus })
  }

  useEffect(() => {
    const hasEditTechnicianLocationPermissions = hasPermission(user).allow(
      PERMISSIONS.TECHNICIANS.CATEGORY,
      PERMISSIONS.TECHNICIANS.ENTRIES.EDIT_LOCATION.NAME,
    )

    const isCreatingNewTechnician = technicianData.objectId === ''

    setShouldAllowLocationEdit(
      hasEditTechnicianLocationPermissions || isCreatingNewTechnician,
    )
  }, [technicianData.objectId, user])

  return (
    <>
      <div className={styles.mainContainer}>
        <div className={styles.flexEnd}>
          <TextField
            label='Employee Id'
            name='employeeId'
            value={employeeId || ''}
            sx={{ width: 150 }}
            onChange={updateTechForm}
          />
          <TextField
            label='Samsara Id'
            name='samsaraId'
            type='number'
            value={samsaraId || ''}
            sx={{ width: 150 }}
            onChange={updateTechForm}
          />
          <TextField
            label='Employment Start Date'
            type='date'
            value={
              employmentStartDate
                ? formatDateYyyyMmDd(new Date(employmentStartDate))
                : ''
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
            onChange={updateStartDate}
            disabled={
              !hasPermission(user).allow(
                PERMISSIONS.TECHNICIANS.CATEGORY,
                PERMISSIONS.TECHNICIANS.ENTRIES.EDIT_EMPLOYMENT_START_DATE.NAME,
              )
            }
          />
          <LocationDropDown
            disabled={!shouldAllowLocationEdit}
            defaultValue={location?.objectId}
            onLocationChange={(location: string) => updateLocation(location)}
          />
        </div>
        <div className={styles.btnsContainer}>
          {inactive ? (
            <button
              className={styles.btn}
              onClick={() => activeStatusHandler(false)}
            >
              Activate
            </button>
          ) : (
            <button
              className={styles.btn}
              onClick={() => activeStatusHandler(true)}
            >
              Deactivate
            </button>
          )}
        </div>
      </div>
    </>
  )
}
